import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Pager horizontal de secciones para pantallas largas en móvil.
 *
 * Muestra una sección a la vez: se pasa a la siguiente deslizando el dedo (CSS scroll-snap,
 * sin dependencias) o tocando/navegando con teclado la barra de pills.
 *
 * Todos los paneles quedan montados en el DOM a propósito: es lo que permite el gesto nativo
 * y lo que hace que los gráficos de recharts (ResponsiveContainer) midan bien su ancho aunque
 * la sección todavía no esté a la vista.
 *
 * @param {Array<{key: string, icon?: string, label: string, content: React.ReactNode}>} sections
 * @param {string} ariaLabel  Etiqueta accesible de la barra de secciones.
 * @param {string} [idPrefix] Prefijo de los ids (por si hay dos pagers en una misma pantalla).
 */
const SectionPager = ({ sections, ariaLabel, idPrefix = 'section-pager' }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    // El carril mediría lo del panel más alto y dejaría un hueco enorme debajo de las secciones
    // cortas, así que sigue la altura de la sección activa.
    const [trackHeight, setTrackHeight] = useState(null);
    const trackRef = useRef(null);
    const tabRefs = useRef([]);
    // Mientras hay un desplazamiento programático en curso guardamos aquí el destino, para que
    // los paneles intermedios que cruza el scroll suave no cambien el pill activo.
    const pendingIndexRef = useRef(null);
    const pendingTimerRef = useRef(null);
    const scrollFrameRef = useRef(null);

    const count = sections.length;

    const clearPending = useCallback(() => {
        pendingIndexRef.current = null;
        if (pendingTimerRef.current) {
            clearTimeout(pendingTimerRef.current);
            pendingTimerRef.current = null;
        }
    }, []);

    // Si el contenido cambia y quedan menos secciones, no dejar el índice fuera de rango.
    useEffect(() => {
        setActiveIndex((prev) => Math.min(prev, Math.max(count - 1, 0)));
    }, [count]);

    useEffect(() => () => {
        if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
        if (scrollFrameRef.current && typeof cancelAnimationFrame === 'function') {
            cancelAnimationFrame(scrollFrameRef.current);
        }
    }, []);

    const goTo = useCallback((index, { focusTab = false } = {}) => {
        const target = Math.max(0, Math.min(index, count - 1));
        setActiveIndex(target);

        const track = trackRef.current;
        if (track) {
            const left = track.clientWidth * target;
            pendingIndexRef.current = target;
            if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
            pendingTimerRef.current = setTimeout(clearPending, 600);

            const reduceMotion = typeof window.matchMedia === 'function'
                && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

            if (typeof track.scrollTo === 'function') {
                track.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
            } else {
                // jsdom no implementa scrollTo, pero sí acepta scrollLeft como propiedad.
                track.scrollLeft = left;
            }
        }

        if (focusTab && tabRefs.current[target]) {
            tabRefs.current[target].focus();
        }
    }, [count, clearPending]);

    // La barra de pills también se desplaza: con 4 secciones el último pill no cabe en pantalla
    // y hay que traerlo a la vista, venga el cambio del gesto o de un toque.
    // block: 'nearest' evita que además se mueva el scroll vertical de la página.
    useEffect(() => {
        const tab = tabRefs.current[activeIndex];
        if (tab && typeof tab.scrollIntoView === 'function') {
            tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
    }, [activeIndex]);

    // El gesto mueve el track; de ahí sale cuál es el pill activo.
    const syncActiveFromScroll = useCallback(() => {
        const track = trackRef.current;
        if (!track || !track.clientWidth) return;

        const index = Math.max(0, Math.min(Math.round(track.scrollLeft / track.clientWidth), count - 1));

        if (pendingIndexRef.current !== null) {
            if (index === pendingIndexRef.current) clearPending();
            return;
        }

        setActiveIndex((prev) => (prev === index ? prev : index));
    }, [count, clearPending]);

    const handleScroll = useCallback(() => {
        if (typeof requestAnimationFrame !== 'function') {
            syncActiveFromScroll();
            return;
        }
        if (scrollFrameRef.current) return;
        scrollFrameRef.current = requestAnimationFrame(() => {
            scrollFrameRef.current = null;
            syncActiveFromScroll();
        });
    }, [syncActiveFromScroll]);

    // Al rotar el teléfono cambia el ancho del track: hay que volver a alinear el panel activo.
    useEffect(() => {
        const handleResize = () => {
            const track = trackRef.current;
            if (!track) return;
            pendingIndexRef.current = activeIndex;
            track.scrollLeft = track.clientWidth * activeIndex;
            if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
            pendingTimerRef.current = setTimeout(clearPending, 600);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [activeIndex, clearPending]);

    // La altura de la sección activa cambia sola cuando un gráfico termina de dibujarse o al
    // rotar el teléfono: por eso se observa el panel en vez de medirlo una sola vez.
    useEffect(() => {
        const track = trackRef.current;
        const panel = track && track.children[activeIndex];
        if (!panel) return undefined;

        const measure = () => setTrackHeight(panel.offsetHeight || null);
        measure();

        if (typeof ResizeObserver !== 'function') return undefined;

        const observer = new ResizeObserver(measure);
        observer.observe(panel);
        return () => observer.disconnect();
    }, [activeIndex, sections]);

    const handleTabsKeyDown = (event) => {
        let target = null;

        if (event.key === 'ArrowRight') target = activeIndex + 1;
        else if (event.key === 'ArrowLeft') target = activeIndex - 1;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = count - 1;

        if (target === null) return;

        event.preventDefault();
        goTo(target, { focusTab: true });
    };

    if (count === 0) return null;

    return (
        <div className="section-pager">
            <div
                className="section-pager__tabs"
                role="tablist"
                aria-label={ariaLabel}
                aria-orientation="horizontal"
                onKeyDown={handleTabsKeyDown}
            >
                {sections.map((section, index) => {
                    const isActive = index === activeIndex;
                    return (
                        <button
                            key={section.key}
                            type="button"
                            role="tab"
                            id={`${idPrefix}-tab-${section.key}`}
                            aria-controls={`${idPrefix}-panel-${section.key}`}
                            aria-selected={isActive}
                            tabIndex={isActive ? 0 : -1}
                            ref={(el) => { tabRefs.current[index] = el; }}
                            className={`section-pager__tab${isActive ? ' section-pager__tab--active' : ''}`}
                            onClick={() => goTo(index)}
                        >
                            {section.icon && <span aria-hidden="true">{section.icon}</span>}
                            <span className="section-pager__tab-label">{section.label}</span>
                        </button>
                    );
                })}
            </div>

            <div
                className="section-pager__track"
                ref={trackRef}
                onScroll={handleScroll}
                style={trackHeight ? { height: `${trackHeight}px` } : undefined}
            >
                {sections.map((section) => (
                    <div
                        key={section.key}
                        className="section-pager__panel"
                        role="tabpanel"
                        id={`${idPrefix}-panel-${section.key}`}
                        aria-labelledby={`${idPrefix}-tab-${section.key}`}
                    >
                        {section.content}
                    </div>
                ))}
            </div>

            <div className="section-pager__dots" aria-hidden="true">
                {sections.map((section, index) => (
                    <span
                        key={section.key}
                        className={`section-pager__dot${index === activeIndex ? ' section-pager__dot--active' : ''}`}
                    />
                ))}
            </div>
        </div>
    );
};

export default SectionPager;
